"use client";

import { useEffect, useRef } from "react";
import {
  useInitializationStatus,
  useLDClient,
  type LDContext,
} from "@launchdarkly/react-sdk";
import { useClaritySession, type SessionState } from "@/lib/auth/auth-store";
import { buildAnonymousLaunchDarklyContext } from "./LaunchDarklyProvider";

type IdentityTarget = {
  context: LDContext;
  signature: string;
};

let activeIdentityBridges = 0;
let anonymousResetGeneration = 0;

function getEnvironment() {
  return process.env.NEXT_PUBLIC_ENVIRONMENT ?? "development";
}

function canSynchronizeIdentity() {
  return (
    typeof window !== "undefined" &&
    window.__E2E_TEST_MODE__ !== true &&
    Boolean(process.env.NEXT_PUBLIC_LAUNCHDARKLY_CLIENT_SIDE_ID?.trim())
  );
}

function getClaimString(
  claims: Record<string, unknown>,
  key: string,
): string | undefined {
  const value = claims[key];

  if (typeof value !== "string") return undefined;

  const trimmed = value.trim();
  return trimmed || undefined;
}

function createIdentityTarget(session: SessionState): IdentityTarget | null {
  if (session.status === "loading") return null;

  if (session.status === "authenticated") {
    const userKey = getClaimString(session.claims, "sub");

    if (userKey) {
      const email = getClaimString(session.claims, "email");
      const context: LDContext = {
        kind: "user",
        key: userKey,
        ...(email ? { email } : {}),
        custom: { environment: getEnvironment() },
      };

      return { context, signature: JSON.stringify(context) };
    }
  }

  const context = buildAnonymousLaunchDarklyContext();
  return { context, signature: JSON.stringify(context) };
}

/**
 * Keeps the singleton LaunchDarkly client synchronized with the authenticated
 * ClarityOS session without recreating the provider or its streaming client.
 */
export function LaunchDarklyIdentityBridge() {
  const session = useClaritySession();
  const ldClient = useLDClient();
  const initialization = useInitializationStatus();
  const attemptedIdentityRef = useRef<string | null>(null);
  const canIdentify =
    initialization.status === "complete" && canSynchronizeIdentity();

  useEffect(() => {
    const target = createIdentityTarget(session);

    if (
      !ldClient ||
      !canIdentify ||
      !target ||
      attemptedIdentityRef.current === target.signature
    ) {
      return;
    }

    attemptedIdentityRef.current = target.signature;

    void ldClient
      .identify(target.context)
      .then((result) => {
        if (result.status === "completed" || result.status === "shed") return;

        console.error(
          "LaunchDarkly identity update did not complete; it will retry after the next session transition.",
          result,
        );

        if (attemptedIdentityRef.current === target.signature) {
          attemptedIdentityRef.current = null;
        }
      })
      .catch((error: unknown) => {
        // Defensive: the v4 browser SDK documents that identify never rejects.
        console.error(
          "LaunchDarkly identity update failed unexpectedly.",
          error,
        );

        if (attemptedIdentityRef.current === target.signature) {
          attemptedIdentityRef.current = null;
        }
      });
  }, [canIdentify, ldClient, session]);

  useEffect(() => {
    if (!ldClient || !canIdentify) return;

    activeIdentityBridges += 1;
    anonymousResetGeneration += 1;

    return () => {
      activeIdentityBridges -= 1;
      const resetGeneration = ++anonymousResetGeneration;
      const anonymousContext = buildAnonymousLaunchDarklyContext();
      const anonymousSignature = JSON.stringify(anonymousContext);

      if (attemptedIdentityRef.current === anonymousSignature) return;

      // Defer by one microtask so React Strict Mode's effect replay, or an
      // immediate authenticated-route remount, can cancel this reset.
      queueMicrotask(() => {
        if (
          activeIdentityBridges > 0 ||
          anonymousResetGeneration !== resetGeneration
        ) {
          return;
        }

        void ldClient
          .identify(anonymousContext)
          .then((result) => {
            if (result.status === "completed" || result.status === "shed") {
              return;
            }

            console.error(
              "LaunchDarkly anonymous identity reset did not complete.",
              result,
            );
          })
          .catch((error: unknown) => {
            console.error(
              "LaunchDarkly anonymous identity reset failed unexpectedly.",
              error,
            );
          });
      });
    };
  }, [canIdentify, ldClient]);

  return null;
}
